// CanvasHistoryContext.tsx
import React, { createContext, useContext, useMemo, useReducer, useRef } from "react";
import type { ReactNode } from "react";
import type { CanvasHistoryApi } from "../services/canvasHistory/canvasHistory.types";
import { createCanvasHistoryStore } from "../services/canvasHistory/canvasHistoryStore";
import type { CanvasHistoryStore } from "../services/canvasHistory/canvasHistoryStore";

const NOOP: CanvasHistoryApi = {
  record: () => undefined, undo: async () => undefined, redo: async () => undefined,
  canUndo: false, canRedo: false, undoLabel: null, redoLabel: null, isBusy: false,
  clear: () => undefined, registerFlusher: () => () => undefined, subscribe: () => () => undefined,
};
const CanvasHistoryContext = createContext<CanvasHistoryApi>(NOOP);

/** One undo/redo stack per mounted story page: key it by the story id. */
export const CanvasHistoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [version, bump] = useReducer((count: number) => count + 1, 0);
  const storeRef = useRef<CanvasHistoryStore | null>(null);
  if (!storeRef.current) storeRef.current = createCanvasHistoryStore(bump);
  const store = storeRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo<CanvasHistoryApi>(() => ({ ...store, ...store.snapshot() }), [store, version]);
  return <CanvasHistoryContext.Provider value={value}>{children}</CanvasHistoryContext.Provider>;
};
/** Outside a provider: a no-op history, so shared hooks keep working on other pages. */
export const useCanvasHistory = (): CanvasHistoryApi => useContext(CanvasHistoryContext);
